import is from '@sindresorhus/is';

import type { ChatResumePayload, ContextRefreshEvent } from '../data-contract/chat';
import type { CobrainerChatAgent } from './cobrainer-chat-agent';
import {
    type ChatAgentSnapshot,
    type ChatLiveActivity,
    type ChatRunSettlement,
    type ChatTurnMemory,
    type ChatUserTurn,
    createChatTurnMemory,
    type DroppedChatEvent,
    EMPTY_CHAT_AGENT_SNAPSHOT,
    ThreadAgentSession,
} from './thread-agent-session';

const CHAT_AGENT_POLL_INTERVAL_MS = 5_000;

/** Idle agents kept besides the visible thread and threads with work in flight. */
const MAX_IDLE_CHAT_AGENTS = 5;

export type ChatAgentRegistryOptions = {
    readonly createAgent: (threadId: string) => CobrainerChatAgent;
    readonly isThreadVisible: (threadId: string) => boolean;
    readonly maxIdleAgents?: number;
    /** Receives every live context refresh; they are never stored, so a sync run never replays one. */
    readonly onContextRefresh: (refresh: ContextRefreshEvent) => void;
    /** Receives stream events that failed validation and were skipped. */
    readonly onDroppedEvent: (dropped: DroppedChatEvent) => void;
    readonly onLiveActivity?: (activity: ChatLiveActivity) => void;
    readonly onRunSettled: (settlement: ChatRunSettlement) => void;
    readonly onRunStarted: () => void;
    readonly pollIntervalMs?: number;
};

const isInitialTurn = (turn: ChatTurnMemory | undefined): boolean =>
    !is.undefined(turn) && !turn.isAgentAction && !turn.isStopped && !turn.sawContextRefresh && !turn.sawVisibleError;

const NO_THREAD_SNAPSHOT: ChatAgentSnapshot = { ...EMPTY_CHAT_AGENT_SNAPSHOT, history: 'loaded' };

/**
 * Owns one ThreadAgentSession per visited thread: creates them on demand, evicts idle ones beyond
 * a bound, and keeps what must outlive a session (the logical turn).
 */
export class ChatAgentRegistry {
    private readonly allListeners = new Set<() => void>();
    private readonly lastUsed = new Map<string, number>();
    private readonly options: ChatAgentRegistryOptions;
    private runningThreadIds: ReadonlySet<string> = new Set();
    private readonly sessions = new Map<string, ThreadAgentSession>();
    private readonly turns = new Map<string, ChatTurnMemory>();
    private useCounter = 0;

    constructor(options: ChatAgentRegistryOptions) {
        this.options = options;
    }

    readonly subscribe = (threadId: string, listener: () => void): (() => void) =>
        this.session(threadId).subscribe(listener);

    readonly subscribeAll = (listener: () => void): (() => void) => {
        this.allListeners.add(listener);
        return () => this.allListeners.delete(listener);
    };

    readonly getSnapshot = (threadId: string | null): ChatAgentSnapshot => {
        if (is.null(threadId)) return NO_THREAD_SNAPSHOT;
        return this.sessions.get(threadId)?.snapshot ?? EMPTY_CHAT_AGENT_SNAPSHOT;
    };

    readonly getRunningThreadIds = (): ReadonlySet<string> => this.runningThreadIds;

    markFresh(threadId: string): void {
        this.session(threadId).markFresh();
    }

    activate(threadId: string): void {
        const session = this.session(threadId);
        this.evictIdleSessions();
        session.activate();
    }

    sync(threadId: string): Promise<void> {
        return this.session(threadId).sync();
    }

    send(threadId: string, build: () => ChatUserTurn): Promise<boolean> {
        return this.session(threadId).send(build);
    }

    /** Answers the thread's open interrupt `interruptId`; see {@link ThreadAgentSession.answer}. */
    answer(threadId: string, interruptId: string, payload: ChatResumePayload): Promise<boolean> {
        return this.session(threadId).answer(interruptId, payload);
    }

    /** Runs the thread's failed latest turn again; see {@link ThreadAgentSession.retry}. */
    retry(threadId: string, userMessageId: string): Promise<boolean> {
        return this.session(threadId).retry(userMessageId);
    }

    stop(threadId: string | null): boolean {
        return is.null(threadId) ? false : (this.sessions.get(threadId)?.stop() ?? false);
    }

    /** Releases one thread's session, or every session without an argument; work in flight is aborted. */
    dispose(threadId?: string): void {
        const threadIds = is.undefined(threadId) ? [...this.sessions.keys()] : [threadId];
        for (const id of threadIds) {
            this.sessions.get(id)?.dispose();
            this.sessions.delete(id);
            this.lastUsed.delete(id);
            // A turn in its initial state carries nothing worth keeping once its session is gone.
            if (isInitialTurn(this.turns.get(id))) this.turns.delete(id);
        }
        this.updateRunningThreadIds();
    }

    private session(threadId: string): ThreadAgentSession {
        this.useCounter += 1;
        this.lastUsed.set(threadId, this.useCounter);
        const existing = this.sessions.get(threadId);
        if (!is.undefined(existing)) return existing;

        const session = new ThreadAgentSession({
            agent: this.options.createAgent(threadId),
            isVisible: () => this.options.isThreadVisible(threadId),
            onChange: () => this.updateRunningThreadIds(),
            onContextRefresh: this.options.onContextRefresh,
            onDroppedEvent: this.options.onDroppedEvent,
            onIdle: () => this.evictIdleSessions(),
            onLiveActivity: this.options.onLiveActivity,
            onRunSettled: this.options.onRunSettled,
            onRunStarted: this.options.onRunStarted,
            pollIntervalMs: this.options.pollIntervalMs ?? CHAT_AGENT_POLL_INTERVAL_MS,
            threadId,
            turn: this.turnOf(threadId),
        });
        this.sessions.set(threadId, session);
        return session;
    }

    private turnOf(threadId: string): ChatTurnMemory {
        const existing = this.turns.get(threadId);
        if (!is.undefined(existing)) return existing;
        const turn = createChatTurnMemory();
        this.turns.set(threadId, turn);
        return turn;
    }

    private evictIdleSessions(): void {
        const idle = [...this.sessions.values()]
            .filter((session) => session.isIdle && !this.options.isThreadVisible(session.threadId))
            .sort(
                (first, second) => (this.lastUsed.get(second.threadId) ?? 0) - (this.lastUsed.get(first.threadId) ?? 0),
            );
        for (const session of idle.slice(this.options.maxIdleAgents ?? MAX_IDLE_CHAT_AGENTS)) {
            this.dispose(session.threadId);
        }
    }

    private updateRunningThreadIds(): void {
        const running = [...this.sessions.values()].filter((session) => session.isRunning).map((s) => s.threadId);
        const isUnchanged =
            running.length === this.runningThreadIds.size && running.every((id) => this.runningThreadIds.has(id));
        if (isUnchanged) return;
        this.runningThreadIds = new Set(running);
        for (const listener of this.allListeners) listener();
    }
}
